import type {
  AirtableSourceConfig,
  ContentSource,
  EmbeddablesCmsSourceConfig,
  Flow,
  Context,
  FlowMetadata,
  ControllerType,
} from '../models/types'

type NormalizedRecord = {
  [key: string]: unknown
}

type EmbeddablesCmsData = {
  tables: {
    id: string
    rows: Array<NormalizedRecord>
  }[]
}

type AirtableRecord = {
  id: string
  createdTime: string
  fields: { [key: string]: unknown }
}
type AirtableResponse = {
  records: AirtableRecord[]
  offset?: string
}

const CMS_KV_BASE_URL = 'https://cms-worker.heysavvy.workers.dev'

export default async function loadExternalContent(
  context: Context,
  flow: Flow,
  flowMetadata: FlowMetadata | {} | undefined,
  url: string,
  isTest: boolean
) {
  const logger = context.logger
  logger?.info({
    scope: 'Load external content',
    functionName: 'loadExternalContent',
    message: 'Starting',
    props: {
      url,
      flowId: flow.id,
      flow_metadata: flowMetadata,
    },
  })
  if (!flow.content_sources) {
    logger?.info({
      scope: 'Load external content',
      functionName: 'loadExternalContent',
      message: 'No content sources found for this embeddable',
      props: {
        flowId: flow.id,
      },
    })
    return
  }

  if (!flowMetadata || !('project_id' in flowMetadata) || !flowMetadata.project_id) {
    logger?.error({
      scope: 'Load external content',
      functionName: 'loadExternalContent',
      message: 'Metadata / Project ID not found',
      props: {
        flowId: flow.id,
      },
    })
    throw new Error('Metadata / Project ID not found')
  }

  const result = (
    await Promise.allSettled(
      flow.content_sources.map(async (source) => {
        try {
          return await getContentSourceData(flow.id, flowMetadata, source, context, url, isTest)
        } catch (e) {
          logger?.error({
            scope: 'Load external content',
            functionName: 'loadExternalContent',
            message: 'Error getting content source data',
            props: {
              source,
              error: e instanceof Error ? e : new Error(String(e)),
            },
          })
          return {}
        }
      })
    )
  ).map((res) => (res.status === 'fulfilled' ? res.value : {}))
  logger?.info({
    scope: 'Load external content',
    functionName: 'loadExternalContent',
    message: 'Completed',
    props: { url, flowId: flow.id },
  })

  return result.reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

async function getContentSourceData(
  flowId: string,
  flowMetadata: FlowMetadata,
  source: ContentSource,
  context: Context,
  url: string,
  isTest: boolean
): Promise<Record<string, unknown>> {
  const { controller_type, controller_key, key, multiple_records } = source

  if (isTest) {
    context.logger?.info({
      scope: 'Load external content',
      functionName: 'getContentSourceData',
      message: 'Returning test data for content source',
      props: { key },
    })
    return {
      [key]: source.multiple_records ? [] : {},
    }
  }

  const allContentSourceData = await getContentSourceDataBySourceType(
    flowId,
    flowMetadata,
    source,
    context,
    url
  )

  const filterFunction = getFilterFunction(controller_type, url, controller_key)

  const filteredContentSourceData = allContentSourceData.filter(filterFunction)
  context.logger?.info({
    scope: 'Load external content',
    functionName: 'getContentSourceData',
    message: 'Filtered content source data',
    props: {
      key,
      filtered_count: filteredContentSourceData.length,
    },
  })

  return {
    [key]: multiple_records ? filteredContentSourceData : filteredContentSourceData[0] || {},
  }
}

async function getContentSourceDataBySourceType(
  flowId: string,
  flowMetadata: FlowMetadata,
  source: ContentSource,
  context: Context,
  url: string
) {
  switch (source.source_type) {
    case 'embeddables_cms': {
      return getEmbeddablesCMSData(flowMetadata, source, context, url)
    }
    case 'airtable': {
      return getAirtableData(flowId, source, context, url)
    }
  }
}

async function getEmbeddablesCMSData(
  flowMetadata: FlowMetadata,
  source: ContentSource,
  context: Context,
  url: string
): Promise<NormalizedRecord[]> {
  const { source_config } = source as ContentSource

  if (!flowMetadata || !('project_id' in flowMetadata) || !flowMetadata.project_id) {
    throw new Error('Metadata / Project ID not found')
  }

  const json = await getFromCmsR2Bucket(context, flowMetadata.project_id, url)

  const table = json.tables.find((table: any) => table.id === source_config.table_id)

  if (!table) {
    throw new Error(`Table not found: ${source_config.table_id}`)
  }

  return table.rows
}

async function getFromCmsR2Bucket(context: Context, projectId: string, url: string) {
  const bucket = context.cmsR2Bucket

  const versionOrStatus = getVersionOrStatusFromUrl(url)

  const path = getFilePath(projectId, versionOrStatus)

  context.logger?.info({
    scope: 'Load external content',
    functionName: 'getFromCmsR2Bucket',
    message: 'Getting CMS data from R2',
    props: {
      path,
      version_or_status: versionOrStatus,
      bucket_exists: Boolean(bucket),
    },
  })

  if (bucket) {
    const objectPath = `${path}.json`

    const object = await bucket.get(objectPath)

    if (!object) {
      throw new Error(`Object not found: ${objectPath}`)
    }

    const data = await object.arrayBuffer()
    const json = JSON.parse(new TextDecoder().decode(data))

    if (!json.tables) {
      throw new Error(`Invalid JSON: ${objectPath}`)
    }

    return json as unknown as EmbeddablesCmsData
  } else {
    if (!CMS_KV_BASE_URL) throw new Error('CMS_KV_BASE_URL is not set')

    const url = `${CMS_KV_BASE_URL}/${path}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      context?.logger?.error({
        scope: 'Load external content',
        functionName: 'getFromCmsR2Bucket',
        message: 'Failed to get CMS data from KV via fetch',
        props: {
          response_status: response.status,
          response_status_text: response.statusText,
        },
      })
      throw new Error('Failed to get CMS data from KV via fetch')
    }

    const json = (await response.json()) as unknown as EmbeddablesCmsData

    return json
  }
}

async function getAirtableData(
  flowId: string,
  source: ContentSource,
  context: Context,
  url: string
): Promise<NormalizedRecord[]> {
  try {
    const { controller_type, controller_key, key, multiple_records } = source as ContentSource
    const source_config = source.source_config as AirtableSourceConfig

    context.logger?.info({
      scope: 'Load external content',
      functionName: 'getAirtableData',
      message: 'Loading content from Airtable for key',
      props: {
        flowId,
        source,
        source_config,
        controller_type,
        controller_key,
        key,
        multiple_records,
      },
    })

    if (controller_type === 'url_pattern') {
      const baseUrl = `https://api.airtable.com/v0/${source_config.base_id}/${source_config.table_id}`
      const config = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${source_config.api_key}`,
        },
      }

      const initialData = await fetchPageOfData<AirtableResponse>(context, baseUrl, config)
      const records = initialData.records

      let offset = initialData.offset
      while (offset) {
        const newData = await fetchPageOfData<AirtableResponse>(
          context,
          baseUrl + `?offset=${offset}`,
          config
        )

        records.push(...newData.records)

        offset = newData.offset
      }
      context.logger?.info({
        scope: 'Load external content',
        functionName: 'getAirtableData',
        message: 'Fetched content data from Airtable',
        props: {
          url,
          controller_key,
          initial_data_length: initialData?.records?.length,
        },
      })

      return records.map((record) => ({ id: record.id, ...record.fields }))
    }
    return []
  } catch (e) {
    context.logger?.error({
      scope: 'Load external content',
      functionName: 'getAirtableData',
      message: 'Error loading content from Airtable',
      props: {
        error: e instanceof Error ? e : new Error(String(e)),
        sourceKey: source?.key,
      },
    })
    return []
  }
}

async function fetchPageOfData<T = any>(context: Context, baseUrl: string, config: RequestInit) {
  const useCache = context.request?.url.searchParams.get('use_cache')
  const cacheTimeout = context.request?.url.searchParams.get('cache_timeout') || 60
  const refreshCache = context.request?.url.searchParams.get('refresh_cache')
  const url = useCache
    ? `https://cache.embeddables.com/?url=${encodeURIComponent(baseUrl)}&timeout=${cacheTimeout}${
        refreshCache ? '&refresh_cache=true' : ''
      }`
    : baseUrl

  context?.logger?.info({
    scope: 'Load external content',
    functionName: 'fetchPageOfData',
    message: 'Fetching CMS content from',
    props: {
      url,
      config,
      use_cache: useCache,
      cache_timeout: cacheTimeout,
      refresh_cache: refreshCache,
    },
  })
  const time = Date.now()
  const result = await fetch(url, config).then((res) => {
    if (res.ok) {
      return res
    }
    context?.logger?.error({
      scope: 'Load external content',
      functionName: 'fetchPageOfData',
      message: 'Error fetching CMS content from URL',
      props: {
        url,
        config,
        use_cache: useCache,
        cache_timeout: cacheTimeout,
        refresh_cache: refreshCache,
        res_status: res.status,
        res_status_text: res.statusText,
      },
    })
    throw new Error(res.statusText)
  })
  const resultData = await result.json()
  const cacheHit = useCache && result?.headers.get('Cf-Cache-Status') === 'HIT'
  context?.logger?.info({
    scope: 'Load external content',
    functionName: 'fetchPageOfData',
    message: 'Fetched CMS content from',
    props: {
      url,
      took: Date.now() - time,
      config,
      use_cache: useCache,
      cache_timeout: cacheTimeout,
      refresh_cache: refreshCache,
      cache_hit: cacheHit,
    },
  })
  return resultData as unknown as T
}

function getFilterFunction(
  controllerType: ControllerType | undefined,
  url: string,
  controllerKey?: string
) {
  if (!controllerType) {
    return () => true
  }

  if (!controllerKey) {
    throw new Error('Controller key is required for controller type: ' + controllerType)
  }

  switch (controllerType) {
    case 'url_pattern':
      return (record: any) => new RegExp(record[controllerKey] as string).test(url)
    case 'url_path_ends_with':
      return (record: any) => url.endsWith(record[controllerKey] as string)
    case 'url_contains':
      return (record: any) => url.includes(record[controllerKey] as string)
    default:
      return () => true
  }
}

function getFilePath(projectId: string, versionOrStatus: string) {
  return `${projectId}/version-${versionOrStatus}`
}

function getVersionOrStatusFromUrl(url: string): string {
  //* Can be a numerical version or a status (PROD or STAGING)
  //* Always returns as a string
  const searchParams = new URL(url).searchParams
  const versionOrStatus = searchParams.get('savvy_cms')
  return versionOrStatus?.toUpperCase() || 'PROD'
}
